import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons'
import { useCallback, useMemo, useState } from 'react'
import { Control, Controller, Path } from 'react-hook-form'
import { Option } from '~/shared/config/constants'
import { filterOptions } from '~/shared/lib/filterOptions'
import { DrawerComponent, useDrawer } from '~/shared/ui/Drawer'
import { RadioGroup } from './RadioGroup'
import {
  Title,
  DrawerWrapper,
  InputWrapper,
  InputIcon,
  VisibleInput,
  SearchInput,
  SearchTitle,
} from './styled'

type SelectProps<TFieldValues extends Record<string, Option>> = {
  name: Path<TFieldValues>
  control?: Control<TFieldValues>
  options: Option[]
  label: string
  modalTitle: string
  queryKey?: unknown[]
  canSearch?: boolean
  disabled?: boolean
  drawerFullScreen?: boolean
}

export function Select<TFieldValues extends Record<string, Option>>({
  name,
  control,
  options,
  label,
  modalTitle,
  canSearch,
  disabled,
  drawerFullScreen = true,
}: SelectProps<TFieldValues>) {
  const [isShow, openDrawer, closeDrawer] = useDrawer()
  const [search, setSearch] = useState<string>('')

  const handleDrawerClose = () => {
    setSearch('')
    closeDrawer()
  }

  const handleInputChange = useCallback(
    (value?: string | null) => {
      setSearch(value || '')
    },
    [setSearch],
  )

  const filteredOptions = useMemo(
    () => (canSearch ? filterOptions(search, options) : options),
    [canSearch, options, search],
  )

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={undefined}
      render={({
        field: { onChange, value },
        fieldState: { error, invalid },
      }) => {
        return (
          <div>
            <InputWrapper onClick={disabled ? undefined : openDrawer}>
              <VisibleInput
                name={name}
                className={invalid ? 'ion-invalid ion-touched' : ''}
                disabled={disabled}
                value={value?.label}
                label={label}
                errorText={error?.message || 'Обязательное поле'}
                labelPlacement='floating'
                fill='outline'
                mode='md'
              />
              <InputIcon
                icon={isShow ? chevronUpOutline : chevronDownOutline}
              />
            </InputWrapper>

            <DrawerComponent
              isShow={isShow}
              onCancel={handleDrawerClose}
              contentProps={
                drawerFullScreen
                  ? {
                      className: 'ion-content-scroll-host',
                      style: { height: '90vh' },
                    }
                  : {}
              }
            >
              <DrawerWrapper>
                <Title>{modalTitle}</Title>

                {canSearch && (
                  <SearchInput
                    label='Поиск'
                    labelPlacement='floating'
                    fill='outline'
                    mode='md'
                    onIonInput={(e) => handleInputChange(e.detail.value)}
                  />
                )}

                {!filteredOptions.length && <div>Ничего не найдено</div>}

                {Boolean(filteredOptions?.length) && (
                  <>
                    {search && <SearchTitle>Результат</SearchTitle>}

                    <RadioGroup
                      data-testid='select-drawer-options'
                      options={filteredOptions}
                      value={value}
                      onChange={(option) => {
                        onChange(option)
                        handleDrawerClose()
                      }}
                    />
                  </>
                )}
              </DrawerWrapper>
            </DrawerComponent>
          </div>
        )
      }}
    />
  )
}
